import { useEffect, useRef } from 'react'

type UseInfiniteScrollOptions<T extends HTMLElement> = {
  enabled: boolean
  onLoadMore: () => void
  rootMargin?: string
  threshold?: IntersectionObserverInit['threshold']
  getRoot?: (sentinel: T) => IntersectionObserverInit['root']
  observeKey?: unknown
}

const useInfiniteScroll = <T extends HTMLElement = HTMLDivElement>({
  enabled,
  getRoot,
  observeKey,
  onLoadMore,
  rootMargin = '200px',
  threshold,
}: UseInfiniteScrollOptions<T>) => {
  const sentinelRef = useRef<T | null>(null)

  useEffect(
    () => {
      const sentinel = sentinelRef.current
      if (!sentinel || !enabled) return

      const observer = new IntersectionObserver(
        entries => {
          if (entries[0]?.isIntersecting) onLoadMore()
        },
        {
          root: getRoot?.(sentinel) ?? null,
          rootMargin,
          threshold,
        },
      )

      observer.observe(sentinel)
      return () => observer.disconnect()
    },
    [
      enabled,
      getRoot,
      observeKey,
      onLoadMore,
      rootMargin,
      threshold,
    ],
  )

  return sentinelRef
}

export { useInfiniteScroll }
